import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import MusicShell from "../fanHome/MusicShell";
import { API_BASE_URL, ACCENT, Field, PrimaryButton, inputClass, ringStyle } from "../artistStudio/shared";
import { fieldErrors, claimErrorText } from "./rightsClaimApi";
import { soundIdFrom } from "./RightsFormPage";

// /rights/takedown (mobile screen 38, "without signing in"): a rights holder with no account sends a copyright notice. Same fields as the
// signed-in notice but the email is required (it is the only way we can answer) and there is no evidence upload. The server limits how
// many a person or connection can send. The case number comes back on screen and by email.
const EMPTY = { sound: "", claimant_name: "", claimant_email: "", original_work_url: "", description: "", reference_number: "", declaration_accepted: false };
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LINK = /^https?:\/\/\S+$/i;

export const validate = (form) => {
  const e = {};
  if (!soundIdFrom(form.sound)) e.sound = "Paste the link of the sound (it ends in /sound/ and a number).";
  if (form.claimant_name.trim().length < 2) e.claimant_name = "Enter your full name.";
  if (!EMAIL.test(form.claimant_email.trim())) e.claimant_email = "Enter a valid email address.";
  if (!LINK.test(form.original_work_url.trim())) e.original_work_url = "Add a link to the original work (it must start with http:// or https://).";
  if (form.description.trim().length < 20) e.description = "Describe what is being infringed (at least 20 characters).";
  if (!form.declaration_accepted) e.declaration_accepted = "Please confirm the declaration to continue.";
  return e;
};

export default function PublicTakedown() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [done, setDone] = useState(null);
  const set = (key) => (event) => setForm((f) => ({ ...f, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    const local = validate(form);
    setErrors(local);
    setMessage("");
    if (Object.keys(local).length) return;
    setBusy(true);
    try {
      const body = {
        track_id: soundIdFrom(form.sound), claimant_name: form.claimant_name.trim(), claimant_email: form.claimant_email.trim(),
        original_work_url: form.original_work_url.trim(), description: form.description.trim(), declaration_accepted: true,
      };
      if (form.reference_number.trim()) body.reference_number = form.reference_number.trim();
      const response = await axios.post(`${API_BASE_URL}/v1/public-takedown`, body, { headers: { "Content-Type": "application/json" }, timeout: 20000 });
      setDone(response.data?.result);
    } catch (e) {
      const fields = fieldErrors(e);
      if (Object.keys(fields).length) setErrors({ ...fields, sound: fields.track_id });
      setMessage(claimErrorText(e, "Your notice could not be sent. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <MusicShell maxWidth="max-w-2xl">
      <Link to="/rights" className="text-sm font-semibold hover:underline" style={{ color: ACCENT }}>← Rights &amp; legal</Link>
      <h1 className="mt-3 text-2xl md:text-3xl font-bold text-gray-900">Copyright takedown notice</h1>
      {done ? (
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5" role="status" data-testid="takedown-done">
          <p className="text-lg font-semibold text-gray-900">Notice received</p>
          <p className="mt-2 text-gray-700">Your case number is <strong>{done.case_number}</strong>. We sent a confirmation to {form.claimant_email.trim()} and will answer you at that address.</p>
          <p className="mt-2 text-sm text-gray-500">Keep the case number: without an account you cannot follow the case here. Have an account? <Link to="/login" className="font-semibold hover:underline" style={{ color: ACCENT }}>Sign in</Link> to add evidence in future.</p>
        </div>
      ) : (
        <form onSubmit={submit} noValidate className="mt-6 space-y-4 rounded-2xl border border-gray-200 bg-white p-5" data-testid="takedown-form">
          <p className="text-sm text-gray-600">Use this if you do not have a Nyawoo account. We cannot take evidence files here, so give a link to your original work and describe the problem clearly. <Link to="/rights/notice" className="font-semibold hover:underline" style={{ color: ACCENT }}>Signed in? File it from your account instead.</Link></p>
          <Field label="Link to the infringing sound" required error={errors.sound}>
            <input value={form.sound} onChange={set("sound")} placeholder="https://…/sound/123" className={inputClass(!!errors.sound)} style={ringStyle} aria-label="Sound link" />
          </Field>
          <Field label="Your full name" required error={errors.claimant_name}>
            <input value={form.claimant_name} onChange={set("claimant_name")} maxLength={150} className={inputClass(!!errors.claimant_name)} style={ringStyle} aria-label="Your full name" />
          </Field>
          <Field label="Your email" required error={errors.claimant_email}>
            <input type="email" value={form.claimant_email} onChange={set("claimant_email")} maxLength={255} className={inputClass(!!errors.claimant_email)} style={ringStyle} aria-label="Your email" />
          </Field>
          <Field label="Link to the original work" required error={errors.original_work_url}>
            <input value={form.original_work_url} onChange={set("original_work_url")} placeholder="https://" maxLength={1000} className={inputClass(!!errors.original_work_url)} style={ringStyle} aria-label="Link to the original work" />
          </Field>
          <Field label="What is being infringed" required error={errors.description}>
            <textarea value={form.description} onChange={set("description")} rows={5} maxLength={2000} className={inputClass(!!errors.description)} style={ringStyle} aria-label="What is being infringed" />
          </Field>
          <Field label="Your reference number (optional)" error={errors.reference_number}>
            <input value={form.reference_number} onChange={set("reference_number")} maxLength={100} className={inputClass(false)} style={ringStyle} aria-label="Your reference number" />
          </Field>
          <label className="flex items-start gap-3 text-sm text-gray-700">
            <input type="checkbox" checked={form.declaration_accepted} onChange={(e) => setForm((f) => ({ ...f, declaration_accepted: e.target.checked }))} className="mt-1" aria-label="Declaration" />
            <span>I confirm, in good faith, that the information in this notice is accurate and that I am the owner of the work, or am authorised to act for the owner.</span>
          </label>
          {errors.declaration_accepted && <p className="text-sm text-red-600" role="alert">{errors.declaration_accepted}</p>}
          {message && <p className="text-sm text-red-600" role="alert">{message}</p>}
          <PrimaryButton type="submit" disabled={busy} className="w-full !py-3">{busy ? "Sending…" : "Send notice"}</PrimaryButton>
        </form>
      )}
    </MusicShell>
  );
}
